/**
 * StepEquipmentClass.jsx
 *
 * Tool Finder wizard step 3 (Explore path). Renders equipment-class tiles
 * sourced from EQUIPMENT_CLASSES_TH or EQUIPMENT_CLASSES_DTH based on the
 * active productLine. Stores the selection in `selections.equipmentClass`.
 *
 * Does NOT auto-advance — NavigationButtons drives Next/Back. The Next
 * handler (handleNextExplore) inspects the selected class:
 *   - threadChoices.length > 0 (TH multi-thread): advance to EQUIPMENT_THREAD_CHOICE
 *   - shankChoices.length > 0 (DTH multi-shank):  advance to EQUIPMENT_THREAD_CHOICE
 *   - else: skip directly to HOLE_DIAMETER
 *
 * Taxonomy ports — see src/data/wizardData.js. TH = 7 cards (5 single-
 * thread + 2 multi-thread). DTH = 4 cards (3 active + rh570 disabled,
 * which is filtered out of the rendered set per the same pattern as
 * StepDrillingMethod's hide-disabled rule).
 *
 * @module components/wizard/StepEquipmentClass
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3c)
 */

import { useWizard } from '@contexts/WizardContext';
import TileSelector from '@components/ui/TileSelector';
import { EQUIPMENT_CLASSES_TH, EQUIPMENT_CLASSES_DTH } from '@data/wizardData';

// Sub-label generators per productLine — UX adds context to the class label.
function thSublabel(cls) {
  if (cls.thread) return `${cls.thread} - ${cls.power || ''}`.replace(/\s*-\s*$/, '');
  if (cls.threadChoices?.length) {
    const threads = cls.threadChoices.map((c) => c.thread).join(' / ');
    return `${threads} - ${cls.power || ''}`.replace(/\s*-\s*$/, '');
  }
  return cls.power || '';
}

function dthSublabel(cls) {
  if (cls.shankChoices?.length) {
    return `${cls.sublabel || ''} - Shanks: ${cls.shankChoices.join(', ')}`;
  }
  return cls.sublabel || '';
}

export default function StepEquipmentClass() {
  const { selections, handleSelect } = useWizard();
  const isDth = selections.productLine === 'DTH';

  // 17 May (Vandana): the rh570 DTH card carries `disabled: true` in wizardData
  // because the hammer series is pre-release. We filter it out here for the
  // same reason StepDrillingMethod hides ITH/Rotary/Raiseboring — the
  // underlying taxonomy stays forward-compatible.
  const source = isDth
    ? EQUIPMENT_CLASSES_DTH.filter((cls) => !cls.disabled)
    : EQUIPMENT_CLASSES_TH;

  const tiles = source.map((cls) => ({
    id: cls.id,
    label: cls.label,
    sublabel: isDth ? dthSublabel(cls) : thSublabel(cls),
    icon: cls.id.slice(0, 3).toUpperCase(),
  }));

  return (
    <section aria-labelledby="equipment-class-heading" className="max-w-5xl mx-auto">
      <header className="mb-6">
        <h2
          id="equipment-class-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black"
        >
          {isDth ? 'Which DTH hammer series?' : 'Which drifter / equipment class?'}
        </h2>
        <p className="mt-1 font-sandvik-text text-n6">
          {isDth
            ? 'Pick the hammer family that matches your rig. Some series support multiple shank types - we will ask next.'
            : 'Pick the equipment class. Most have a single thread; the specialty classes will ask you to pick one next.'}
        </p>
      </header>

      <TileSelector
        tiles={tiles}
        value={selections.equipmentClass}
        onChange={(id) => {
          // Clear any previously picked thread/shank when the user changes
          // their class, since the choices may no longer apply.
          // BUGFIX 18 May: also flip requiresEquipmentThreadChoice so the
          // progress strip inserts the conditional sub-step (or removes it
          // when the user switches to a single-thread/single-shank class).
          // Without this, getProgressConfig falls back to the base 6-step
          // sequence and the dots / counter mislabel the current step.
          const eqClass = source.find((c) => c.id === id) || null;
          const hasChoices = !!(
            (eqClass && eqClass.threadChoices && eqClass.threadChoices.length) ||
            (eqClass && eqClass.shankChoices && eqClass.shankChoices.length)
          );
          handleSelect('equipmentClass', id, {
            threadSystem: null,
            shankType: null,
            requiresEquipmentThreadChoice: hasChoices,
          });
        }}
        cols={{ mobile: 2, tablet: 3, desktop: 3 }}
        variant="icon"
        ariaLabel="Equipment class"
      />
    </section>
  );
}
